import { useEffect, useMemo, useState } from 'react';
import { RotationMap } from '../components/ledger/Marathon';
import type { MarathonAccount, MarathonBoard, MarathonCohort } from '../lib/api';
import { INK, ICE, TEAL, RIM, TITLE_FONT, PX_FONT, pixelFrame, arcadeTitleShadow } from '../components/guild/theme';

type Seed = [label: string, cohort: MarathonCohort, role: 'master' | 'sub', status: 'eval' | 'funded'];

/* Account line-up at each of the playbook's eight steps */
const f = 'funded' as const, e = 'eval' as const;
const A = (st: ('eval' | 'funded')[]): Seed[] => [['MA', 'A', 'master', st[0]], ['sA1', 'A', 'sub', st[1]], ['sA2', 'A', 'sub', st[2]], ...(st[3] ? [['sA3', 'A', 'sub', st[3]] as Seed] : []), ...(st[4] ? [['sA4', 'A', 'sub', st[4]] as Seed] : [])];
const cohort = (c: 'B' | 'C' | 'D', st: ('eval' | 'funded')[]): Seed[] =>
  st.map((s, i) => (i === 0 ? [`M${c}`, c, 'master', s] : [`s${c}${i}`, c, 'sub', s]) as Seed);
const allF = [f, f, f, f, f];

const STEPS: { n: number; title: string; blurb: string; seeds: Seed[] }[] = [
  { n: 1, title: 'Start with three — the Pair', blurb: 'MA + sA1 + sA2, all evaluations. MA is in every trade; the two subs swap.', seeds: A([e, e, e]) },
  { n: 2, title: 'Hit the trigger — the Cluster', blurb: 'The first three are funded; buy sA3 and sA4. Three accounts fire, two sit out.', seeds: A([f, f, f, e, e]) },
  { n: 3, title: 'Cohort A pays — buying Cohort B', blurb: 'All five of A funded and firing together. Its first payout opens MB + sB1 + sB2.', seeds: [...A(allF), ...cohort('B', [e, e, e])] },
  { n: 4, title: 'B hits its trigger', blurb: 'MB, sB1, sB2 funded — buy sB3 and sB4. A keeps taking full payouts.', seeds: [...A(allF), ...cohort('B', [f, f, f, e, e])] },
  { n: 5, title: 'Cohort B completes — now two cohorts pay', blurb: 'B is whole. The MOAT buys all five of Cohort C on the spot.', seeds: [...A(allF), ...cohort('B', allF), ...cohort('C', [e, e, e, e, e])] },
  { n: 6, title: 'Cohort C runs its evals — four trades deep', blurb: 'A, B, then C in two halves: the rotation is four trades deep.', seeds: [...A(allF), ...cohort('B', allF), ...cohort('C', [e, e, e, e, e])] },
  {
    n: 7, title: 'The Restructure — masters form their own cohort',
    blurb: 'Every 100k moves into the Master Cohort (plus two new 100k evals); A, B and C backfill with a new 50k each. Rotation: M + cA → M + cB → M + cC.',
    seeds: [
      ['MA', 'M', 'master', f], ['MB', 'M', 'master', f], ['MC', 'M', 'master', f], ['M4', 'M', 'master', e], ['M5', 'M', 'master', e],
      ...(['A', 'B', 'C'] as const).flatMap(c => [1, 2, 3, 4, 5].map(i => [`s${c}${i}`, c, 'sub', i === 5 ? e : f] as Seed)),
    ],
  },
  {
    n: 8, title: 'The last cohort — twenty-five accounts',
    blurb: 'All twenty funded, so Cohort D is bought: five 50k evals. Terminal rotation: M + cA → M + cB → M + cC → M + cD.',
    seeds: [
      ...[1, 2, 3, 4, 5].map(i => [`M${i}`, 'M', 'master', f] as Seed),
      ...(['A', 'B', 'C', 'D'] as const).flatMap(c => [1, 2, 3, 4, 5].map(i => [`s${c}${i}`, c, 'sub', c === 'D' ? e : f] as Seed)),
    ],
  },
];

function toAccount([label, c, role, status]: Seed): MarathonAccount {
  const size = role === 'master' ? 100000 : 50000;
  const mdd = size === 100000 ? 3000 : 2000;
  const balance = status === 'funded' ? size + mdd * 0.9 : size + mdd * 0.4;
  return {
    id: `${c}-${label}`, label, cohort: c, role, size, firm: 'Apex', status, balance, peak: balance, mdd,
    floor: balance - mdd, locked: status === 'funded', iced: false, iced_until: null, room: mdd,
    target: status === 'eval' ? size + mdd * 1.5 : null, targetHit: false, payoutReady: false, nextPayout: 0, payouts_taken: 0, platform_id: null, payoutLine: null, dll: null, trail: 'intraday',
  };
}

/* Mirrors backend services/marathon.ts rotationSlots */
function slots(accounts: MarathonAccount[]) {
  const by = (c: MarathonCohort) => accounts.filter(a => a.cohort === c);
  const subNo = (a: MarathonAccount) => Number(a.label.match(/(\d+)$/)?.[1] ?? 0);
  const out: { label: string; accountIds: string[] }[] = [];
  const masters = by('M');
  for (const c of ['A', 'B', 'C', 'D'] as MarathonCohort[]) {
    const g = by(c);
    if (!g.length) continue;
    if (masters.length) { out.push({ label: `M + c${c}`, accountIds: [...masters, ...g].map(a => a.id) }); continue; }
    if (g.length >= 5 && g.every(a => a.status === 'funded')) { out.push({ label: `Cohort ${c}`, accountIds: g.map(a => a.id) }); continue; }
    const m = g.filter(a => a.role === 'master');
    const subs = g.filter(a => a.role === 'sub');
    out.push({ label: `Cohort ${c} · slot 1`, accountIds: [...m, ...subs.filter(s => subNo(s) % 2 === 1)].map(a => a.id) });
    out.push({ label: `Cohort ${c} · slot 2`, accountIds: [...m, ...subs.filter(s => subNo(s) % 2 === 0)].map(a => a.id) });
  }
  return out;
}

/** Steps 1–8 of the build-out. With `onClose` it runs as a full-screen tutorial you step through (Esc closes). */
export function MarathonPhasesPreview({ onClose }: { onClose?: () => void } = {}) {
  const modal = Boolean(onClose);
  const [step, setStep] = useState(1);
  const [slot, setSlot] = useState(0);
  const [auto, setAuto] = useState(false);

  // Demo controls: ← / → change step, space advances a trade
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') { setStep(x => Math.min(8, x + 1)); setSlot(0); }
      if (e.key === 'ArrowLeft') { setStep(x => Math.max(1, x - 1)); setSlot(0); }
      if (e.key === ' ') { e.preventDefault(); setSlot(x => x + 1); }
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Autoplay: two trades per step, then on to the next
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      setSlot(x => {
        if (x >= 1) { setStep(st => (st >= 8 ? 1 : st + 1)); return 0; }
        return x + 1;
      });
    }, 2200);
    return () => clearInterval(id);
  }, [auto]);
  const board = useMemo(() => {
    const accounts = STEPS[step - 1].seeds.map(toAccount);
    const sl = slots(accounts);
    const idx = sl.length ? slot % sl.length : 0;
    return {
      accounts,
      rotation: { slots: sl, next: sl.length ? { index: idx, label: sl[idx].label, firing: sl[idx].accountIds, sittingOut: [] } : null },
    } satisfies Pick<MarathonBoard, 'accounts' | 'rotation'>;
  }, [step, slot]);
  const s = STEPS[step - 1];
  const btn = (on: boolean) => ({
    fontFamily: TITLE_FONT, fontSize: 18, padding: '8px 14px', border: 'none', cursor: 'pointer',
    background: on ? 'var(--pal-tab-on)' : 'var(--pal-tab-off)', color: on ? 'var(--pal-tab-on-text)' : TEAL,
    boxShadow: on ? `0 0 0 2px ${TEAL}, 0 0 0 4px ${INK}` : `0 0 0 2px ${RIM}, 0 0 0 4px ${INK}`,
  });

  return (
    <div role={modal ? 'dialog' : undefined} aria-modal={modal || undefined} aria-label={modal ? 'Marathon Method tutorial' : undefined}
      style={modal
        ? { position: 'fixed', inset: 0, zIndex: 120, overflowY: 'auto', background: 'var(--pal-page)', padding: '28px 24px 48px', animation: 'pal-swap-in 0.35s ease-out both' }
        : { minHeight: '100vh', background: 'var(--pal-page)', padding: '32px 24px 64px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>
        {modal && (
          /* Video-style progress: one segment per step, the current one filling as it plays */
          <div style={{ display: 'flex', gap: 6 }} aria-hidden>
            {STEPS.map(x => (
              <div key={x.n} style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--pal-field)', overflow: 'hidden' }}>
                <div key={`${step}-${auto}`} style={{
                  height: '100%', background: 'var(--pal-accent)',
                  width: x.n < step ? '100%' : x.n > step ? '0%' : undefined,
                  animation: x.n === step && auto ? 'pal-fill 4.4s linear both' : undefined,
                }} />
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h1 className="pal-title" style={{ margin: 0, fontFamily: TITLE_FONT, fontSize: 36, letterSpacing: '0.1em', color: ICE, textShadow: arcadeTitleShadow }}>MARATHON · <span className="pal-em">3 → 25</span></h1>
          {modal && (
            <button type="button" onClick={onClose} aria-label="Close tutorial"
              style={{ ...btn(false), fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>Close ✕</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {STEPS.map(x => <button key={x.n} type="button" style={btn(x.n === step)} onClick={() => { setStep(x.n); setSlot(0); }}>{x.n}</button>)}
        </div>
        <section style={{ background: 'var(--pal-panel-glass)', padding: 20, boxShadow: pixelFrame() }}>
          <div style={{ fontFamily: TITLE_FONT, fontSize: 24, color: ICE, textShadow: "var(--pal-head-shadow)" }}>Step {s.n} · {s.title}</div>
          <div style={{ fontFamily: PX_FONT, fontSize: 11, lineHeight: 1.7, color: TEAL, margin: '6px 0 16px' }}>
            {s.blurb} <span style={{ color: ICE }}>({board.accounts.length} accounts)</span>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
            <button type="button" style={{ ...btn(false), fontSize: 14 }} onClick={() => setSlot(x => x + 1)}>Advance one trade →</button>
            <button type="button" style={{ ...btn(auto), fontSize: 14 }} onClick={() => setAuto(a => !a)}>{auto ? '❚❚ Pause' : '▶ Autoplay'}</button>
            <span style={{ fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.12em', color: TEAL }}>← → change step · space = next trade</span>
          </div>
          <RotationMap board={board} />
          {modal && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 22, gap: 12 }}>
              <button type="button" disabled={step === 1} onClick={() => { setStep(x => x - 1); setSlot(0); }}
                style={{ ...btn(false), fontSize: 15, opacity: step === 1 ? 0.4 : 1 }}>← Back</button>
              <span style={{ fontFamily: PX_FONT, fontSize: 12, color: TEAL }}>Step {step} of 8</span>
              {step < 8 ? (
                <button type="button" onClick={() => { setStep(x => x + 1); setSlot(0); }}
                  style={{ ...btn(true), fontSize: 15 }}>Next step →</button>
              ) : (
                <button type="button" onClick={onClose} style={{ ...btn(true), fontSize: 15 }}>Finish ✓</button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
